// THI Case Setup Guide content. Layout lives in components/hospital/texas-heart/CaseSetup.js.
//
//   categories[]  { id, label, procedures[] }
//   procedure     { id, label (chip), cards[] }                       -> one or more cards
//               | { id, label, title, comingSoon: true, note? }      -> placeholder until written
//   card          { title, sections[] }; section { heading, items[] }
//   item          "text" | { text, action }   (action opens an in-app tool, e.g. "eras")
//
// To add a procedure: add an object to its category. To add a category: add it here and
// to CASE_TYPE_ROWS in HospitalDetailScreen. Search covers titles, labels and item text.
// "needsReview": content carried over from an earlier draft; confirm before relying on it.

import { expandQuery, textMatchesAny } from '../../utils/expandAbbreviations';

export const thiCases = [
  {
    "id": "cardiac",
    "label": "Cardiac",
    "procedures": [
      {
        "id": "sternotomy-cpb",
        "label": "Sternotomy CPB",
        "cards": [
          {
            "title": "Cardiac Case with CPB (Sternotomy)",
            "sections": [
              {
                "heading": "Anesthetic Type",
                "items": [
                  "GETA"
                ]
              },
              {
                "heading": "Monitors",
                "items": [
                  "Standard ASA Monitors",
                  "Arterial Line (usually pre-induction in the OR)",
                  "Central Venous Catheter",
                  "R2 Defibrillation Pads",
                  "Transesophageal Echo",
                  "Consider NIRS (discuss with attending)"
                ]
              },
              {
                "heading": "Vascular Access",
                "items": [
                  "MAC with PA Catheter vs. Companion Catheter"
                ]
              },
              {
                "heading": "Special Considerations",
                "items": [
                  {
                    "text": "Enhanced Recovery Pathway Measures",
                    "action": "eras"
                  },
                  "Consider acute normovolemic hemodilution (ANH)",
                  "Consider AFib Prophylaxis Protocol: 2 grams Magnesium after CPB",
                  "Minimal / no paralytics after CPB"
                ]
              }
            ]
          }
        ]
      },
      {
        "id": "robotic-cpb",
        "label": "Robotic CPB",
        "cards": [
          {
            "title": "Cardiac Case with CPB (Robotic / Minimally Invasive)",
            "sections": [
              {
                "heading": "Anesthetic Type",
                "items": [
                  "GETA"
                ]
              },
              {
                "heading": "Airway",
                "items": [
                  "Double-Lumen Endotracheal Tube"
                ]
              },
              {
                "heading": "Monitors",
                "items": [
                  "Standard ASA Monitors",
                  "Arterial Line (usually pre-induction in the OR)",
                  "Central Venous Catheter",
                  "R2 Defibrillation Pads",
                  "Transesophageal Echo",
                  "Consider NIRS (discuss with attending)"
                ]
              },
              {
                "heading": "Vascular Access",
                "items": [
                  "MAC with Companion (rarely PA Catheter)",
                  "Consider Right IJ 7Fr sheath if over 80kg",
                  "↳ Would be rewired for SVC CPB cannula. Place close to clavicle",
                  "Consider Right IJ Transvenous Pacemaker",
                  "↳ If redo case (for VFib arrest) or if non-robotic minimally invasive"
                ]
              },
              {
                "heading": "Special Considerations",
                "items": [
                  {
                    "text": "Enhanced Recovery Pathway Measures",
                    "action": "eras"
                  },
                  "ESP Nerve Block Catheter",
                  "Airway Exchange to Single-Lumen ETT at End of Case",
                  "Consider AFib Prophylaxis Protocol: 2 grams Magnesium after CPB",
                  "Minimal / no paralytics after CPB"
                ]
              }
            ]
          }
        ]
      },
      {
        "id": "robo-lima-lad",
        "label": "Robo LIMA-LAD",
        "cards": [
          {
            "title": "Robotic CABG (LIMA-LAD)",
            "sections": [
              {
                "heading": "Anesthetic Type",
                "items": [
                  "GETA"
                ]
              },
              {
                "heading": "Airway",
                "items": [
                  "DLT or Single Lumen ETT with Bronchial Blocker"
                ]
              },
              {
                "heading": "Monitors",
                "items": [
                  "Standard ASA Monitors",
                  "Arterial Line (usually pre-induction in the OR)",
                  "R2 Defibrillation Pads",
                  "Transesophageal Echo",
                  "Consider NIRS (discuss with attending)"
                ]
              },
              {
                "heading": "Vascular Access",
                "items": [
                  "MAC with Companion (rarely PA Catheter)"
                ]
              },
              {
                "heading": "Special Considerations",
                "items": [
                  "Usually performed off-pump",
                  "VA ECMO used if low LVEF or large heart size (check CT)",
                  "Consider ERACS Meds",
                  "ESP Nerve Block Catheter",
                  "Consider extubation in the OR if appropriate"
                ]
              }
            ]
          }
        ]
      },
      {
        "id": "aata",
        "label": "AATA",
        "cards": [
          {
            "title": "Ascending Aortic and/or Aortic Arch Repair (AATA)",
            "sections": [
              {
                "heading": "Anesthetic Type",
                "items": [
                  "GETA"
                ]
              },
              {
                "heading": "Monitors",
                "items": [
                  "Standard ASA Monitors",
                  "Arterial Line (usually pre-induction in the OR) - frequently LEFT RADIAL",
                  "Femoral Arterial Line (to be placed by surgeons)",
                  "R2 Defibrillation Pads",
                  "Transesophageal Echo",
                  "NIRS",
                  "Consider BIS (to view raw EEG waveform; discuss with attending)"
                ]
              },
              {
                "heading": "Vascular Access",
                "items": [
                  "MAC with PA Catheter"
                ]
              },
              {
                "heading": "Additional Medications",
                "items": [
                  "Nicardipine - Infusion & Syringe (0.1 mg/mL)",
                  "Neuroprotection for Circ Arrest: consider Phenobarbital or Propofol",
                  "Consider Vasopressin if likely to need &gt;1 vasopressor"
                ]
              },
              {
                "heading": "Special Considerations",
                "items": [
                  "Frequently bilateral radial arterial lines if total arch repair (vs. hemi-arch)",
                  "Ice applied to the head for circ arrest (obtain from Perfusion)",
                  "Consider acute normovolemic hemodilution (ANH)"
                ]
              }
            ]
          }
        ]
      }
    ]
  },
  {
    "id": "vascular-aortic",
    "label": "Vascular/Aortic",
    "procedures": [
      {
        "id": "taaa",
        "label": "TAAA",
        "cards": [
          {
            "title": "Thoracoabdominal Aortic Aneurysm Repair (TAAA)",
            "sections": [
              {
                "heading": "Anesthetic Type",
                "items": [
                  "GETA"
                ]
              },
              {
                "heading": "Airway",
                "items": [
                  "Double-Lumen Endotracheal Tube"
                ]
              },
              {
                "heading": "Monitors",
                "items": [
                  "Standard ASA Monitors",
                  "Arterial Line (usually pre-induction in the OR) - RIGHT BRACHIAL",
                  "R2 Defibrillation Pads",
                  "Consider NIRS (depending on anatomy)",
                  "Discuss Lumbar CSF Drain (and ICP Monitoring) with Surgeon"
                ]
              },
              {
                "heading": "Vascular Access",
                "items": [
                  "9 French single lumen introducer + PA Catheter",
                  "12 French triple lumen CVC",
                  "Belmont Rapid Infusion FMS - often with \"splitter\""
                ]
              },
              {
                "heading": "Additional Medications",
                "items": [
                  "Nicardipine - Infusion & Syringe (0.1 mg/mL). May need 0.25 mg/mL too",
                  "Sodium bicarbonate infusion (1 mEq/mL; order from Pharmacy)",
                  "Consider mannitol 25 grams or 0.5 grams/kg",
                  "Consider Bludigo (indigo carmine)",
                  "↳ To monitor urinary transit time after reperfusion"
                ]
              },
              {
                "heading": "Special Considerations",
                "items": [
                  "NG Tube (not OG)",
                  "Do not do airway exchange at the end, patient keeps DLT in ICU"
                ]
              }
            ]
          }
        ]
      },
      {
        "id": "aaa",
        "label": "AAA",
        "cards": [
          {
            "title": "Abdominal Aortic Aneurysm Repair (Open)",
            "sections": [
              {
                "heading": "Anesthetic Type",
                "items": [
                  "GETA"
                ]
              },
              {
                "heading": "Monitors",
                "items": [
                  "Standard ASA Monitors",
                  "Arterial Line (usually pre-induction in the OR)",
                  "R2 Defibrillation Pads"
                ]
              },
              {
                "heading": "Vascular Access",
                "items": [
                  "MAC with Companion Catheter",
                  "Consider Belmont Rapid Infusion FMS - often with single arm"
                ]
              },
              {
                "heading": "Additional Medications",
                "items": [
                  "Nicardipine - Infusion & Syringe (0.1 mg/mL)",
                  "Mannitol 25 grams or 0.5 grams/kg",
                  "Consider heparin infusion (25,000 units in 250 mL) if long AoXC time anticipated"
                ]
              },
              {
                "heading": "Special Considerations",
                "items": [
                  "NG Tube (not OG)",
                  "May extubate select patients in the OR; usually stay intubated to CVRR"
                ]
              }
            ]
          }
        ]
      },
      {
        "id": "tevar",
        "label": "TEVAR",
        "cards": [
          {
            "title": "Thoracic Endovascular Aortic Repair (TEVAR)",
            "sections": [
              {
                "heading": "Anesthetic Type",
                "items": [
                  "GETA"
                ]
              },
              {
                "heading": "Monitors",
                "items": [
                  "Standard ASA Monitors",
                  "Arterial Line (usually pre-induction in the OR) - frequently RIGHT radial",
                  "Consider NIRS (depending on anatomy)",
                  "Discuss Lumbar CSF Drain (and ICP Monitoring) with Surgeon"
                ]
              },
              {
                "heading": "Vascular Access",
                "items": [
                  "Central line is necessary",
                  "Consider MAC with Companion vs. Smaller Line, e.g. 9 French Triple Lumen or 8 French Double Lumen CVC"
                ]
              },
              {
                "heading": "Additional Medications",
                "items": [
                  "Nicardipine - Infusion & Syringe (0.1 mg/mL)",
                  "Heparin infusion (25,000 units in 250 mL) (Oderich, Maximus)",
                  "Mannitol 25 grams or 0.5 grams/kg (Oderich, Maximus)"
                ]
              },
              {
                "heading": "Special Considerations",
                "items": [
                  "Coselli, Orozco, Xue: Neuromonitoring not normally used",
                  "Oderich, Maximus: Discuss Whether Neuromonitoring (MEPs, SSEPs, EEG) will be Used with Surgeon",
                  "Plan for extubation in the OR"
                ]
              }
            ]
          }
        ]
      },
      {
        "id": "evar",
        "label": "EVAR",
        "title": "Endovascular Aortic Repair (EVAR)",
        "comingSoon": true
      },
      {
        "id": "zone0-tevar",
        "label": "Aortic Arch EVAR",
        "cards": [
          {
            "title": "Endovascular Aortic Arch Repair (\"Zone 0\" TEVAR)",
            "sections": [
              {
                "heading": "Anesthetic Type",
                "items": [
                  "GETA"
                ]
              },
              {
                "heading": "Monitors",
                "items": [
                  "Standard ASA Monitors",
                  "Arterial Line (usually pre-induction in the OR) - frequently LEFT radial",
                  "↳ Surgeon may need to access right axillary artery for procedure",
                  "Consider NIRS"
                ]
              },
              {
                "heading": "Vascular Access",
                "items": [
                  "Left IJ Central Venous Access",
                  "9 French Triple Lumen vs 8 French Double Lumen CVC",
                  "Consider Left IJ Transvenous Pacemaker (if Needed)"
                ]
              },
              {
                "heading": "Additional Medications",
                "items": [
                  "Nicardipine - Infusion & Syringe (0.1 mg/mL)",
                  "Heparin infusion (25,000 units in 250 mL)",
                  "May need mannitol 25 grams or 0.5 grams/kg"
                ]
              },
              {
                "heading": "Special Considerations",
                "items": [
                  "Discuss whether Rapid Pacing will be Needed with Surgeon"
                ]
              }
            ]
          }
        ]
      }
    ]
  },
  {
    "id": "transplant",
    "label": "Transplant",
    "procedures": [
      {
        "id": "heart",
        "label": "Heart",
        "cards": [
          {
            "title": "Heart Transplant",
            "sections": [
              {
                "heading": "Anesthetic Type",
                "items": [
                  "GETA"
                ]
              },
              {
                "heading": "Monitors",
                "items": [
                  "Standard ASA Monitors",
                  "Arterial Line (usually pre-induction in the OR)",
                  "R2 Defibrillation Pads",
                  "Transesophageal Echo",
                  "Consider NIRS (discuss with attending)"
                ]
              },
              {
                "heading": "Vascular Access",
                "items": [
                  "MAC with PA Catheter"
                ]
              },
              {
                "heading": "Additional Medications",
                "items": [
                  "Immunosuppression:",
                  "- Methylprednisolone 1000 mg",
                  "- Mycophenolate (ordered by Cardiologist, delivered by Pharmacy)",
                  "- Basiliximab 20mg - reconstitute in 100mL and administer slowly",
                  "Inhaled nitric oxide",
                  "Dobutamine",
                  "Vasopressin",
                  "Consider isoproterenol (2 mg in 125 mL; order from Pharmacy)"
                ]
              },
              {
                "heading": "Special Considerations",
                "items": [
                  "If existing lines are old, replace them (infection risk)",
                  "PAC must be withdrawn during cardiectomy back into SVC",
                  "↳ Place drips on central line side port instead of VIP lumen on PAC, otherwise will be extracorporeal"
                ]
              }
            ]
          }
        ]
      },
      {
        "id": "lung",
        "label": "Lung",
        "cards": [
          {
            "title": "Lung Transplant",
            "sections": [
              {
                "heading": "Anesthetic Type",
                "items": [
                  "GETA"
                ]
              },
              {
                "heading": "Airway",
                "items": [
                  "Double Lumen Endotracheal Tube"
                ]
              },
              {
                "heading": "Monitors",
                "items": [
                  "Standard ASA Monitors",
                  "Arterial Line (usually pre-induction in the OR)",
                  "R2 Defibrillation Pads",
                  "Transesophageal Echo",
                  "Consider NIRS (discuss with attending)"
                ]
              },
              {
                "heading": "Vascular Access",
                "items": [
                  "MAC with PA Catheter"
                ]
              },
              {
                "heading": "Additional Medications",
                "items": [
                  "Immunosuppression:",
                  "- Methylprednisolone 1000 mg",
                  "- Mycophenolate (ordered by Pulmonologist, delivered by Pharmacy)",
                  "- Basiliximab 20mg - reconstitute in 100mL and administer slowly",
                  "Inhaled nitric oxide vs Inhaled Epoprostenol",
                  "Consider Dobutamine to help RV tolerate PA clamping",
                  "Vasopressin"
                ]
              },
              {
                "heading": "Special Considerations",
                "items": [
                  "If existing lines are old, replace them (infection risk)",
                  "May be done off pump or on VA ECMO",
                  "Airway exchange to single lumen ETT at end, at least size 8.0 for Bronch"
                ]
              }
            ]
          }
        ]
      },
      {
        "id": "liver",
        "label": "Liver",
        "cards": [
          {
            "title": "Liver Transplant",
            "sections": [
              {
                "heading": "Anesthetic Type",
                "items": [
                  "GETA"
                ]
              },
              {
                "heading": "Monitors",
                "items": [
                  "Standard ASA Monitors",
                  "Arterial Line (pre- or post-induction in the OR)",
                  "R2 Defibrillation Pads"
                ]
              },
              {
                "heading": "Vascular Access",
                "items": [
                  "MAC with Companion Catheter",
                  "Consider Second CVC if High Risk of Bleeding",
                  "Belmont Rapid Infusion FMS - often with \"splitter\""
                ]
              },
              {
                "heading": "Additional Medications",
                "items": [
                  "Sodium bicarbonate infusion (1 mEq/mL; order from Pharmacy)",
                  "Immunosuppression: hydrocortisone 1000 mg",
                  "Antibiotics:",
                  "- cefazolin 2 grams, or",
                  "- Vancomycin, piperacillin-tazobactam, and fluconazole",
                  "Dopamine",
                  "Lots of calcium chloride, sodium bicarbonate, and albumin bottles!"
                ]
              },
              {
                "heading": "Special Considerations",
                "items": [
                  "Intraop CRRT if poor renal function (use 2K dialysate, not 4K!)",
                  "If we place HD catheter: always place on right (straight shot)",
                  "NG Tube (not OG)",
                  "Will stay intubated postoperatively"
                ]
              }
            ]
          }
        ]
      },
      {
        "id": "kidney",
        "label": "Kidney",
        "cards": [
          {
            "title": "Kidney Transplant",
            "sections": [
              {
                "heading": "Anesthetic Type",
                "items": [
                  "GETA"
                ]
              },
              {
                "heading": "Monitors",
                "items": [
                  "Standard ASA Monitors",
                  "Arterial Line (pre- or post-induction in the OR)"
                ]
              },
              {
                "heading": "Vascular Access",
                "items": [
                  "9 French Triple Lumen vs 8 French Double Lumen CVC"
                ]
              },
              {
                "heading": "Additional Medications",
                "items": [
                  "Immunosuppression:",
                  "- methylprednisolone 500 mg",
                  "- Thymo or Basiliximab (ordered by Nephrologist)",
                  "Antibiotics: cefazolin 2 grams",
                  "Dopamine",
                  "Furosemide 100mg at reperfusion",
                  "Mannitol 25 grams at reperfusion"
                ]
              },
              {
                "heading": "Special Considerations",
                "items": [
                  "Maintain renal perfusion pressure, MAP &gt; 80-85 after reperfusion",
                  "↳ Ideally without vasopressors: dopamine, IV fluids, etc.",
                  "Plan to extubate at end of procedure"
                ]
              }
            ]
          }
        ]
      }
    ]
  },
  {
    "id": "mcs",
    "label": "MCS",
    "procedures": [
      {
        "id": "impella",
        "label": "Impella",
        "title": "Impella Placement",
        "comingSoon": true
      },
      {
        "id": "ecmo",
        "label": "ECMO",
        "title": "ECMO Placement / Revision",
        "comingSoon": true
      },
      {
        "id": "lvad",
        "label": "LVAD",
        "title": "LVAD Placement",
        "comingSoon": true,
        "note": "Overlaps with Sternotomy CPB setup"
      },
      {
        "id": "bivacor",
        "label": "BiVACOR TAH",
        "title": "BiVACOR Total Artificial Heart",
        "comingSoon": true
      },
      {
        "id": "syncardia",
        "label": "SynCardia TAH",
        "title": "SynCardia Total Artificial Heart",
        "comingSoon": true
      }
    ]
  },
  {
    "id": "cath-lab",
    "label": "Cath Lab",
    "procedures": [
      {
        "id": "tavr",
        "label": "TAVR",
        "cards": [
          {
            "title": "TAVR",
            "sections": [
              {
                "heading": "Anesthetic Type",
                "items": [
                  "- Bandeali: usually GETA, may request MAC in select patients",
                  "- Fish: always GETA",
                  "- Kracjer: always MAC",
                  "- Massumi: always GETA",
                  "- Mortazavi: always GETA",
                  "- Postalian: prefers MAC but does not mind if patient needs GETA",
                  "- Sen: usually GETA, may request MAC in select patients",
                  "- Silva: in flux. Usually GETA, requesting MAC more often",
                  "- Strickman: prefers MAC but does not mind if patient needs GETA"
                ]
              },
              {
                "heading": "Monitors",
                "items": [
                  "Standard ASA Monitors",
                  "Arterial Line (pre- or post-induction in the OR) - usually RIGHT radial",
                  "↳ Exception: Krajcer (he will pass off femoral arterial access)",
                  "↳ Strickman will access right radial sheath; place NIBP on left arm",
                  "↳ Silva will access right radial sheath; place bilateral arterial lines",
                  "Transesophageal Echo (GETA) or Transthoracic Echo (MAC)"
                ]
              },
              {
                "heading": "Vascular Access",
                "items": [
                  "Right IJ 8 French Single lumen Introducer (accessed by cardiologist for Transvenous Pacemaker Placement)",
                  "↳ Exception: Krajcer (he will place his own femoral venous TVP)",
                  "Additional Large-Bore PIV"
                ]
              },
              {
                "heading": "Additional Medications",
                "items": [
                  "- Remifentanil infusion (0.05 mcg/kg/min is a decent starting point)",
                  "- Dexmedetomidine or Propofol Infusion",
                  "- +/- Midazolam"
                ]
              },
              {
                "heading": "Special Considerations",
                "items": [
                  "Plan to extubate at the end (if GETA)",
                  "Cardiac surgeon on standby"
                ]
              }
            ]
          }
        ]
      },
      {
        "id": "ep-ablation",
        "label": "EP Ablation",
        "cards": [
          {
            "title": "Atrial Fibrillation (AFib) Ablation",
            "sections": [
              {
                "heading": "Anesthetic Type",
                "items": [
                  "GETA"
                ]
              },
              {
                "heading": "Monitors",
                "items": [
                  "Standard ASA Monitors",
                  "Often only non-invasive monitors are needed"
                ]
              },
              {
                "heading": "Vascular Access",
                "items": [
                  "Additional Large-Bore PIV"
                ]
              },
              {
                "heading": "Additional Medications",
                "items": [
                  "Phenylephrine infusion"
                ]
              },
              {
                "heading": "Special Considerations",
                "items": [
                  "- Radiofrequency (RFA): no paralysis beyond intubation (phrenic nerve), esophageal temp probe",
                  "- Cryoablation (\"Cryo\"): no paralysis beyond intubation (phrenic nerve), esophageal temp probe",
                  "- Pulsed Field Ablation (PFA): keep patient paralyzed, monitor any temp",
                  "- Farapulse PFA: keep patient paralyzed, cardiologist may request 1L fluid bolus and glycopyrrolate up front"
                ]
              }
            ]
          },
          {
            "title": "Ventricular Tachycardia (VT) Ablation",
            "sections": [
              {
                "heading": "Anesthetic Type",
                "items": [
                  "GETA"
                ]
              },
              {
                "heading": "Monitors",
                "items": [
                  "Standard ASA Monitors",
                  "Arterial Line (usually pre- or post-induction in the OR)",
                  "R2 Defibrillation Pads",
                  "Transesophageal Echo"
                ]
              },
              {
                "heading": "Vascular Access",
                "items": [
                  "9 French Triple Lumen vs 8 French Double Lumen CVC"
                ]
              },
              {
                "heading": "Special Considerations",
                "items": [
                  "Cardiologists need to induce VT - may need hemodynamic support during long runs of VT (sometimes hours)"
                ]
              }
            ]
          },
          {
            "title": "PVC Ablation",
            "sections": [
              {
                "heading": "Anesthetic Type",
                "items": [
                  "MAC"
                ]
              },
              {
                "heading": "Monitors",
                "items": [
                  "Standard ASA Monitors",
                  "Often only non-invasive monitors are needed"
                ]
              },
              {
                "heading": "Vascular Access",
                "items": [
                  "Single PIV"
                ]
              },
              {
                "heading": "Special Considerations",
                "items": [
                  "Most sedative / anesthetic agents can mask PVCs. Frequently will start with no sedation of any kind for mapping. Set patient expectations. Discuss sedation with cardiologist."
                ]
              }
            ]
          }
        ]
      },
      {
        "id": "lead-extraction",
        "label": "Lead Extraction",
        "cards": [
          {
            "title": "Laser Lead Extraction",
            "sections": [
              {
                "heading": "Anesthetic Type",
                "items": [
                  "GETA"
                ]
              },
              {
                "heading": "Monitors",
                "items": [
                  "Standard ASA Monitors",
                  "Arterial Line (usually pre- or post-induction in the OR)",
                  "R2 Defibrillation Pads",
                  "Transesophageal Echo"
                ]
              },
              {
                "heading": "Vascular Access",
                "items": [
                  "Consider central line placement",
                  "Large-bore IV access (frequently in RIGHT upper extremity; contralateral to device)",
                  "Obtain femoral venous access passed off by Cardiologist"
                ]
              },
              {
                "heading": "Special Considerations",
                "items": [
                  "Potential risk of injury to left subclavian vein, innominate vein, SVC, & RA",
                  "Cardiac surgeon on standby",
                  "Perfusion on standby",
                  "Cardiologist may place occlusive balloon in SVC to tamponade bleeding if injury occurs - need to ensure fluids/meds reach the patient (i.e. femoral)"
                ]
              }
            ]
          }
        ],
        "needsReview": true
      },
      {
        "id": "devices",
        "label": "Devices",
        "title": "Devices (AICD / Pacemaker)",
        "comingSoon": true
      },
      {
        "id": "structural",
        "label": "Structural",
        "title": "Structural (ASD/PFO, LAAO)",
        "comingSoon": true
      }
    ]
  },
  {
    "id": "specialty",
    "label": "Specialty",
    "procedures": [
      {
        "id": "convergent",
        "label": "Convergent",
        "cards": [
          {
            "title": "Convergent Procedure: Epicardial AFib Ablation + LAA Ligation",
            "sections": [
              {
                "heading": "Anesthetic Type",
                "items": [
                  "GETA"
                ]
              },
              {
                "heading": "Airway",
                "items": [
                  "Double Lumen Endotracheal Tube"
                ]
              },
              {
                "heading": "Monitors",
                "items": [
                  "Standard ASA Monitors",
                  "Arterial Line (usually pre- or post-induction in the OR)",
                  "R2 Defibrillation Pads",
                  "Transesophageal Echo"
                ]
              },
              {
                "heading": "Vascular Access",
                "items": [
                  "9 French Triple Lumen vs 8 French Double Lumen CVC",
                  "+/- additional PIV"
                ]
              },
              {
                "heading": "Additional Medications",
                "items": [
                  "Consider methylprednisolone 125mg for postop pericarditis pain"
                ]
              },
              {
                "heading": "Special Considerations",
                "items": [
                  "Frequently put patient to sleep with non-invasive monitors, then place TEE probe to evaluate LAA. If no thrombus, OK to proceed with line placement (case will be canceled if LAA thrombus)",
                  "Start with subxiphoid incision for epicardial ablation, then left VATS for LAA ligation",
                  "Circa esophageal temperature probe needed during ablation (TEE pulled back but not totally removed)",
                  "Position with pressure bag under left scapula to bump pt for LAA ligation"
                ]
              }
            ]
          }
        ]
      },
      {
        "id": "pte",
        "label": "PTE",
        "title": "Pulmonary Thromboendarterectomy (PTE)",
        "comingSoon": true
      }
    ]
  }
];

/** Flat list of every procedure with its category, for search */
export const allProcedures = () =>
  thiCases.flatMap((cat) => cat.procedures.map((p) => ({ ...p, categoryId: cat.id, categoryLabel: cat.label })));

/** Search over titles, chip labels and card content; abbreviations expand both ways (TEE, AAA, …) */
export const searchCases = (query) => {
  const terms = expandQuery(query);
  if (!terms.length || terms[0].length < 2) return [];
  const matches = (text) => textMatchesAny(text, terms);
  return allProcedures()
    .map((p) => {
      const title = p.title || (p.cards || []).map((c) => c.title).join(' · ');
      const texts = (p.cards || []).flatMap((c) => c.sections.flatMap((s) => s.items.map((i) => (typeof i === 'string' ? i : i.text))));
      if (![title, p.label, ...texts].some(matches)) return null;
      const hit = texts.find(matches);
      return { procedure: p, title, snippet: hit && !matches(title) && !matches(p.label) ? hit : null };
    })
    .filter(Boolean);
};
